import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  Outlet,
} from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import BackToTop from "./components/BackToTop";
import Navbar from "./components/Navbar";
import DashboardNavbar from "./components/DashboardNavbar"; // You'll need to create this

import Login from "./pages/Login"; // Keep for now or lazy
import Home from "./pages/Home";
import About from "./pages/About";
import Programs from "./pages/Programs";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import Gallery from "./pages/Gallery";

// Lazy loaded secondary/protected pages
const RegisterPage = lazy(() => import("./pages/register/RegisterPage"));
const InterestForm = lazy(() => import("./pages/interest/InterestForm"));
const TrialInterestForm = lazy(() => import("./pages/interest/TrialInterestForm"));
const SignUp = lazy(() => import("./pages/Signup"));
const Dashboard = lazy(() => import("./pages/Dashboard.jsx"));
const HoldingPage = lazy(() => import("./pages/HoldingPage"));
const ExpiredPage = lazy(() => import("./pages/ExpiredPage"));
const SuspendedPage = lazy(() => import("./pages/SuspendedPage"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const NotFound = lazy(() => import("./pages/NotFound"));

import ProtectedRoute from "./components/ProtectedRoute";
import Footer from "./components/Footer";
import TrialPopup from "./components/TrialPopup";
import ErrorBoundary from "./components/ErrorBoundary";
import OfflineOverlay from "./components/OfflineOverlay";
import useOnlineStatus from "./hooks/useOnlineStatus";
import { getAuthService } from "./firebase/services";
import { Loader2 } from "lucide-react";

const ADMIN_EMAIL = "reflectionfutbolacademy@gmail.com";

// Loading component for Suspense
const PageLoader = () => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center">
    <Loader2 className="animate-spin text-[#F4B400] mb-4" size={48} />
    <p className="font-inter font-bold uppercase tracking-widest text-white/60">
      Loading...
    </p>
  </div>
);

const AdminProtectedRoute = ({ children }) => {
  const [checking, setChecking] = React.useState(true);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    let unsub = null;
    let cancelled = false;

    const init = async () => {
      const auth = await getAuthService();
      const { onAuthStateChanged } = await import("firebase/auth");
      if (cancelled) return;
      unsub = onAuthStateChanged(auth, (user) => {
        if (user && user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
        setChecking(false);
      });
    };

    init();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  // Best-effort: terminate admin session if the tab/browser is closed while in admin.
  React.useEffect(() => {
    if (!isAdmin) return;

    const terminate = () => {
      getAuthService().then((auth) => auth.signOut()).catch(() => {});
    };

    window.addEventListener("beforeunload", terminate);
    window.addEventListener("pagehide", terminate);

    return () => {
      window.removeEventListener("beforeunload", terminate);
      window.removeEventListener("pagehide", terminate);
    };
  }, [isAdmin]);

  if (checking) {
    return (
      <div className="min-h-[100dvh] bg-[#131314] flex flex-col items-center justify-center">
        <Loader2 className="animate-spin text-[#F4B400] mb-4" size={48} />
        <p className="font-inter font-bold uppercase tracking-widest text-white/60">
          Securing Access...
        </p>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
};

const AdminPublicRoute = ({ children }) => {
  const [checking, setChecking] = React.useState(true);
  const [isAdmin, setIsAdmin] = React.useState(false);

  React.useEffect(() => {
    let unsub = null;
    let cancelled = false;

    const init = async () => {
      const auth = await getAuthService();
      const { onAuthStateChanged } = await import("firebase/auth");
      if (cancelled) return;
      unsub = onAuthStateChanged(auth, (user) => {
        if (user && user.email.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
        setChecking(false);
      });
    };

    init();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, []);

  if (checking) return null;
  if (isAdmin) return <Navigate to="/admin" replace />;
  return children;
};

const AdminLayout = () => <Outlet />;

const AppContent = () => {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith("/dashboard");
  const isAdminPage = location.pathname.startsWith("/admin");
  const isApplyPage = location.pathname === "/apply";
  const isOnline = useOnlineStatus();
  const prevIsAdminPageRef = React.useRef(isAdminPage);

  // Toggle offline class on body
  React.useEffect(() => {
    if (!isOnline) {
      document.body.classList.add("is-offline");
    } else {
      document.body.classList.remove("is-offline");
    }
  }, [isOnline]);

  // Terminate admin session if user navigates away from /admin.
  React.useEffect(() => {
    const wasAdmin = prevIsAdminPageRef.current;
    if (wasAdmin && !isAdminPage) {
      getAuthService()
        .then((auth) => {
          const user = auth.currentUser;
          if (user && user.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase()) {
            return auth.signOut();
          }
          return null;
        })
        .catch(() => {});
    }
    prevIsAdminPageRef.current = isAdminPage;
  }, [isAdminPage]);

  return (
    <div className="flex flex-col min-h-[100dvh]">
      {!isOnline && <OfflineOverlay isAdmin={isAdminPage} />}
      <ScrollToTop />
      <BackToTop />
      {!isDashboard && !isAdminPage && <TrialPopup />}

      {isDashboard ? (
        <DashboardNavbar />
      ) : isAdminPage ? null : (
        <Navbar />
      )}

      <main
        className={`flex-grow ${
          isApplyPage || isAdminPage ? "bg-[#f5f5f5]" : ""
        }`}
      >
        <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/programs" element={<Programs />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/apply" element={<RegisterPage />} />
              <Route path="/interest-form" element={<InterestForm />} />
              <Route path="/trial-interest" element={<TrialInterestForm />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />

              <Route path="/holding" element={<HoldingPage />} />
              <Route path="/expired" element={<ExpiredPage />} />
              <Route path="/suspended" element={<SuspendedPage />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />

              {/* Standalone Admin Module */}
              <Route
                path="/admin/login"
                element={
                  <AdminPublicRoute>
                    <AdminLogin />
                  </AdminPublicRoute>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <AdminProtectedRoute>
                    <AdminLayout />
                  </AdminProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
              </Route>

              {/* 404 Not Found */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      {/* Hide footer when inside the dashboard or admin page */}
      {!isDashboard && !isAdminPage && <Footer />}
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;
